import React from "react";
import { Box, Typography, Divider, Container, Paper } from "@mui/material";

/**
 * Shared layout component for legal and information pages
 * @param {Object} props
 * @param {string} props.title - The page title
 * @param {React.ReactNode} props.children - The page content
 * @param {string} [props.subtitle] - Optional subtitle (like effective date)
 * @returns {JSX.Element}
 */
const LegalPageLayout = ({ title, children, subtitle }) => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Paper
        elevation={0}
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 5 },
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontSize: { xs: "1.75rem", sm: "2.25rem" },
            fontWeight: 600,
            mb: 1,
            color: "text.primary",
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {subtitle}
          </Typography>
        )}

        <Divider sx={{ my: { xs: 2, sm: 3 } }} />

        <Box
          sx={{
            "& p": {
              fontSize: { xs: "0.875rem", sm: "1rem" },
              lineHeight: 1.8,
              mb: { xs: 1.5, sm: 2 },
            },
            "& h2": {
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              fontWeight: 600,
              mt: { xs: 3, sm: 4 },
              mb: { xs: 1, sm: 2 },
              color: "text.primary",
            },
            "& ul": {
              pl: { xs: 2, sm: 3 },
              mb: { xs: 2, sm: 3 },
            },
            "& li": {
              fontSize: { xs: "0.875rem", sm: "1rem" },
              mb: 1,
              lineHeight: 1.6,
            },
            "& a": {
              color: "primary.main",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            },
            "& strong": {
              fontWeight: 600,
            },
          }}
        >
          {children}
        </Box>
      </Paper>
    </Container>
  );
};

export default LegalPageLayout;
